import { Component, OnInit, Type, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { SortablejsOptions } from 'angular-sortablejs';
import { ToasterService } from 'angular2-toaster';
import { DeleteModalComponent } from 'app/components/modal/deleteModal.component';
import { ConfigService } from 'app/services/config.service';
import { StorageCategories } from 'app/services/database/storage.service';
import { LoadImage } from 'app/services/loadImage';
import { ModalService } from 'app/services/modal.service';
import { CustomValidators } from 'app/shared/customValidators';
import { FileModel, FileModelBuilder } from 'app/views/presentations/models/file.model';
import { DisplayMode, SlideshowAnimation, SlideshowModel, TransitionTime } from 'app/views/presentations/models/slideshow.model';
import { Widget } from 'app/views/presentations/widgets/widget';
import { WidgetComponent } from 'app/views/presentations/widgets/widget.component';
import { Observable } from 'rxjs';

export class SlideshowWidget extends Widget {
    public type: string = 'Slideshow';
    public icon: string = 'fa-image';
    public nameResourceKey: string = 'modules.editors.slideshow.name';
    public componentType: Type<WidgetComponent> = SlideshowWidgetComponent;
}

@Component({
    selector: 'psm-slideshow-widget',
    templateUrl: 'slideshow.widget.html',
    styleUrls: ['slideshow.widget.scss']
})

export class SlideshowWidgetComponent extends WidgetComponent<SlideshowModel> implements OnInit {
    public acceptedFileTypes;
    public defaultDisplayMode: string;
    public maxFileSize: number;
    public isLoading: boolean = true;
    public sortableOptions: SortablejsOptions = {};
    public form: FormGroup;

    @ViewChild('fileselector') public fileSelector;

    public readonly animations: string[] = Object.keys(SlideshowAnimation);
    public readonly transitions: string[] = Object.keys(TransitionTime);
    public readonly displayModes: string[] = Object.keys(DisplayMode);

    public slideshowForm: FormControl = new FormControl('slideshow', (validator) => {
        if (this.files.length > 0) return;
        return { 'modules.editors.slideshow.slideshow-required': true };
    });

    constructor(private configService: ConfigService, private modalService: ModalService, private toasterService: ToasterService, private loadImage: LoadImage) {
        super();
    }

    public async initialize() {
        this.content = !this.content ? new SlideshowModel(DisplayMode.Fill, SlideshowAnimation.FadeInFadeOut, TransitionTime.Normal) : this.content;

        this.form = new FormGroup({
            animation: new FormControl(this.content.animation, CustomValidators.isInEnum(SlideshowAnimation, 'modules.editors.slideshow.no-animation-error')),
            transition: new FormControl(this.content.transition, CustomValidators.isInEnum(TransitionTime, 'modules.editors.slideshow.no-transition-speed-error')),
            displayMode: new FormControl(this.content.displayMode, CustomValidators.isInEnum(DisplayMode, 'modules.editors.slideshow.no-display-mode-error')),
            files: new FormControl(this.files, (control) => this.validateFiles(control))
        });
    }

    public async ngOnInit() {
        this.acceptedFileTypes = await this.configService.get('acceptedImageFile', ['.jpg', '.png', '.bmp']);
        this.maxFileSize = await this.configService.get('maxImageFileSize', 16);

        this.isLoading = false;
    }

    public async addImage(pic: File) {
        if (!pic) { return; }
        if (pic.type === 'image/jpeg') {

            const metadata = await this.loadImage.parseJpegMetadata(pic);
            let orientation = 0;
            if (metadata.exif) {
                orientation = metadata.exif.get('Orientation');
            }

            const canvas = await this.loadImage.loadJpeg(pic, orientation);
            const base64data = canvas.toDataURL('image/jpeg');
            const blob = await this.loadImage.canvasToBlob(canvas);
            this.files.push(new FileModelBuilder({ name: pic.name, file: blob, url: base64data, size: pic.size, category: StorageCategories.Images }));
            
        } else {
            this.files.push(new FileModelBuilder({ name: pic.name, file: pic, size: pic.size, category: StorageCategories.Images }, true));
        }
        this.fileSelector.reset();
        this.form.get('files').markAsTouched();
        this.form.get('files').updateValueAndValidity();
        this.contentChanged.emit(WidgetComponent.contentChangedEventName);
    }

    private validateFiles(control: AbstractControl) {
        return !this.files || this.files.length === 0 ? { 'modules.editors.slideshow.no-image-error': true } : null;
    }

    public async openDeleteModal(image: FileModel) {
        let deleteModalResponse = await this.modalService.open(DeleteModalComponent, {
            title: 'modules.editors.slideshow.delete-comfirm'
        });

        if (deleteModalResponse) {
            this.files = this.files.filter(obj => obj !== image);
            this.form.get('files').markAsTouched();
            this.form.get('files').updateValueAndValidity();
        }
    }

    public onMaxFileSizeExceeded() {
        this.toasterService.pop('error', '', 'modules.super-admin.configurations.file-exceeds-maximum-size');
    }

    public onFileTypeInvalid() {
        this.toasterService.pop('error', '', 'modules.super-admin.configurations.file-type-invalid');
    }

    public async getFormControls(): Promise<AbstractControl[]> {
        return [this.form];
    }
}


// WEBPACK FOOTER //
// ./src/app/views/presentations/widgets/slideshow/slideshow.widget.ts